import { Table } from 'antd'
import { ColumnsType, ExpandableConfig } from 'antd/es/table/interface'
import { Breakpoint } from 'antd/lib/_util/responsiveObserver'
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'

import {
  Maybe,
  MonitoringCuzkRcFilter,
  useAllMonitoringCuzkRcsQuery,
} from '../../../graphql/generated'
import { useAppSelector } from '../../../redux/hooks'
import {
  birthDateRenderer,
  checkFrequencyRenderer,
  cuzkOs,
  fullnameFilter,
  fullnameRenderer,
  fullnameSorter,
  labelRenderer,
  lastCheckRenderer,
  nextCheckRenderer,
  rcIcoRenderer,
  validatedRenderer,
} from '../../utils/cuzkMonitorRcTableHelpers'
import {
  booleanSorter,
  labelSorter,
  numberSorter,
  stringSorter,
  utcDateSorter,
  utcDescDateSorter,
} from '../../utils/generalTableHelpers'
import { LabelConnectionFragment } from '../../utils/labelsTypes'
import { tablePagination } from '../../utils/layoutConst'
import { cuzkMonitorRcCreatePath } from '../../utils/paths'
import { useColumnFilters } from '../../utils/useColumnFilters'
import { useHandleExpandRow } from '../../utils/useHandleExpandRow'
import { useTableRowSelection } from '../../utils/useTableRowSelection'
import { CuzkMonitorRcTableAction } from '../CuzkMonitorRcTableAction/CuzkMonitorRcTableAction'
import { CuzkMonitorRcTableExpandedRow } from '../CuzkMonitorRcTableExpandedRow/CuzkMonitorRcTableExpandedRow'
import { CuzkMonitorRcTableStatusCell } from '../CuzkMonitorRcTableStatusCell/CuzkMonitorRcTableStatusCell'
import { Min } from '../DateFormat/DateFormat'
import { ExpandColumnTitle } from '../ExpandColumnTitle/ExpandColumnTitle'
import { ExpandIcon } from '../ExpandIcon/ExpandIcon'
import { TableBulkEditPanel } from '../TableBulkEditPanel/TableBulkEditPanel'
import { TableEmptyText } from '../TableEmptyText/TableEmptyText'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'
import { TableSummaryExport } from '../TableSummaryExport/TableSummaryExport'

export interface RcTableItem {
  id: string
  description?: string
  rc?: string
  ico?: string
  firstname?: string
  lastname?: string
  birthDate?: string
  typed?: string
  validatedCode?: number
  checkFrequency?: number
  lastCheck?: string
  nextCheck?: string
  found?: boolean
  labels: LabelConnectionFragment
  cuzkOByCuzkOsId?: Maybe<cuzkOs>
  createdAt: Date
}

export interface CuzkMonitorRcTableProps {
  MonitoringCuzkRcFilter?: MonitoringCuzkRcFilter
}

const tableId = 'cuzk-subjekty-table'
const scroll = { x: '1200' }

export const CuzkMonitorRcTable: FunctionComponent<CuzkMonitorRcTableProps> =
  props => {
    const tenantId = useAppSelector(state => state.myProfile.tenantId)
    const expandedRowKeys = useAppSelector(
      state => state.expandedRow.tables?.[tableId] || []
    )

    const handleExpandRow = useHandleExpandRow(tableId)
    const {
      searchFilter,
      dualSearchFilter,
      customSearchFilter,
      dateFilter,
      labelFilter,
    } = useColumnFilters<RcTableItem>()

    const [loadingId, setLoadingId] = useState<string[]>([])

    const [queryResult, reexecuteQuery] = useAllMonitoringCuzkRcsQuery({
      variables: { accountId: tenantId, filter: props.MonitoringCuzkRcFilter },
    })

    const dataSource = useMemo(
      () =>
        (queryResult?.data?.allMonitoringCuzkRcs?.nodes || []) as RcTableItem[],
      [queryResult?.data?.allMonitoringCuzkRcs?.nodes]
    )

    const expandableRecords = useMemo(
      () =>
        dataSource.filter(record => !!record.found).map(record => record.id),
      [dataSource]
    )

    const {
      selectedRows: selectedRow,
      rowSelection,
      resetSelection,
    } = useTableRowSelection<RcTableItem>({ getRowKey: record => record.id })

    const columns = useMemo<ColumnsType<RcTableItem>>(
      () => [
        {
          title: 'ID',
          dataIndex: 'id',
          responsive: ['' as Breakpoint],
        },
        {
          title: 'Přidáno',
          dataIndex: 'createdAt',
          defaultSortOrder: 'descend',
          sorter: utcDateSorter('createdAt'),
          ...dateFilter('createdAt'),
          render: createdAt => <Min>{createdAt}</Min>,
        },
        {
          title: 'Štítek',
          dataIndex: 'labels',
          sorter: labelSorter('labels'),
          ...labelFilter('labels'),
          render: labelRenderer,
        },
        {
          title: 'Poznámka',
          dataIndex: 'description',
          sorter: stringSorter('description'),
          ...searchFilter('description', 'Vyhledat v textu poznámky'),
        },
        {
          title: 'RČ / IČO',
          dataIndex: 'rc',
          render: rcIcoRenderer,
          ...dualSearchFilter('rc', 'ico', 'Vyhledat RČ nebo IČO'),
          sorter: {
            compare: (a, b) =>
              a.rc && b.rc
                ? a.rc.toString().localeCompare(b.rc.toString())
                : a.ico && b.ico
                ? a.ico.toString().localeCompare(b.ico.toString())
                : 0,
          },
        },
        {
          title: 'Jméno',
          dataIndex: 'fullname',
          ...customSearchFilter(fullnameFilter, 'Vyhledat dle jména'),
          render: fullnameRenderer,
          sorter: fullnameSorter,
        },
        {
          title: 'Datum nar.',
          dataIndex: 'birthDate',
          render: birthDateRenderer,
          sorter: {
            compare: (a, b) =>
              a.rc && b.rc ? a.rc.toString().localeCompare(b.rc.toString()) : 0,
          },
        },
        {
          title: 'Validace RČ',
          dataIndex: 'validated',
          render: validatedRenderer,
        },
        {
          title: 'Frekvence kontroly',
          dataIndex: 'checkFrequency',
          sorter: numberSorter('checkFrequency'),
          render: checkFrequencyRenderer,
        },
        {
          title: 'Nadcházející kontrola',
          dataIndex: 'nextCheck',
          // sorter: stringSorter('nextCheck'), TODO
          render: nextCheckRenderer,
        },
        {
          title: 'Poslední kontrola',
          dataIndex: 'lastCheck',
          sorter: utcDescDateSorter('lastCheck'),
          render: lastCheckRenderer,
        },
        {
          title: 'Stav',
          dataIndex: 'found',
          sorter: booleanSorter('found'),
          render: (state: boolean | undefined, record: RcTableItem) => (
            <CuzkMonitorRcTableStatusCell
              state={state}
              record={record}
              loadingId={loadingId}
              handleExpandRow={handleExpandRow}
            />
          ),
        },
        {
          title: 'Kód validace',
          dataIndex: 'validatedCode',
          responsive: ['' as Breakpoint],
        },
        {
          title: 'Akce',
          dataIndex: 'actions',
          render: (_: string, record: RcTableItem) => (
            <CuzkMonitorRcTableAction
              record={record}
              reexecuteQuery={reexecuteQuery}
              setLoadingId={setLoadingId}
            />
          ),
        },
      ],
      [
        customSearchFilter,
        dateFilter,
        dualSearchFilter,
        handleExpandRow,
        labelFilter,
        loadingId,
        reexecuteQuery,
        searchFilter,
      ]
    )

    const locale = useMemo(() => {
      return {
        emptyText: queryResult.fetching ? (
          <TableSkeleton rows={10} />
        ) : (
          <TableEmptyText
            buttonText="Vyhledat subjekt"
            buttonLink={cuzkMonitorRcCreatePath()}
            text="Zatím jste nevyhledali žádné subjekty v ČÚZK."
          />
        ),
      }
    }, [queryResult.fetching])

    const onBulkEdit = useCallback(() => {
      resetSelection()
      reexecuteQuery()
    }, [reexecuteQuery, resetSelection])

    const expandable = useMemo<ExpandableConfig<RcTableItem>>(() => {
      return {
        expandedRowRender: (record: RcTableItem) => (
          <CuzkMonitorRcTableExpandedRow
            className="pl-12"
            cuzkOs={record.cuzkOByCuzkOsId}
          />
        ),
        expandedRowKeys,
        columnTitle: (
          <ExpandColumnTitle allKeys={expandableRecords} tableId={tableId} />
        ),
        expandIcon: ({ expanded, record }) => (
          <ExpandIcon
            expanded={expanded}
            recordId={record.id}
            tableId={tableId}
            expandable={!!record.found}
          />
        ),
      }
    }, [expandableRecords, expandedRowKeys])

    const summary = useCallback(
      () => (
        <TableSummaryExport
          total={dataSource?.length || 0}
          colSpan={13}
          url="cuzk/cuzkOsXlsx"
          fileName="CUZK-osoby"
          dataSource={dataSource || []}
          icon
        />
      ),
      [dataSource]
    )

    return (
      <>
        <TableBulkEditPanel
          setLoadingId={setLoadingId}
          refetch={onBulkEdit}
          selectedRows={selectedRow}
          resetSelection={resetSelection}
        />
        <Table
          rowKey="id"
          rowClassName="animate-fadeIn"
          dataSource={dataSource}
          columns={columns}
          loading={queryResult.fetching}
          expandable={expandable}
          locale={locale}
          rowSelection={rowSelection}
          size="small"
          summary={summary}
          pagination={tablePagination}
          scroll={scroll}
        />
      </>
    )
  }
